import { createContext, useContext, useState, useEffect } from "react";
import DashboardLayout from "./pages/DashboardLayout";

import { collection, onSnapshot, doc, setDoc, updateDoc, getDoc, increment } from "firebase/firestore";
import { db } from "./firebase"; 

const StockContext = createContext();
export const useStock = () => useContext(StockContext);

const GlobalResponsiveStyles = () => (
  <style>{`
    /* Basic Reset */
    * { box-sizing: border-box; }
    body, html { margin: 0; padding: 0; overflow-x: hidden; background: #f8fafc; }
    .grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
    .grid-2 { display: grid; grid-template-columns: 2fr 1fr; gap: 16px; }
    .flex-resp { display: flex; gap: 16px; flex-wrap: wrap; }
    .table-responsive { width: 100%; overflow-x: auto; padding-bottom: 12px; }
    .sidebar-wrapper { position: fixed; top: 0; bottom: 0; left: 0; z-index: 1000; width: 270px; background: #0b0f19; transition: transform 0.3s; }
    .main-wrapper { flex: 1; display: flex; flex-direction: column; height: 100vh; margin-left: 270px; width: calc(100% - 270px); }
    @media (max-width: 1024px) { .sidebar-wrapper { transform: translateX(-100%); } .sidebar-wrapper.open { transform: translateX(0); } .main-wrapper { margin-left: 0; width: 100%; } }
  `}</style>
);

export default function App() {
  const [balances, setBalances] = useState({ "FFC Alipay": 0, "FFC Wechat": 0 });
  const [notifications, setNotifications] = useState({ emailAlerts: true, stockAlerts: true, salesReports: false, newCustomers: true });
  const [profile, setProfile] = useState({ name: "", email: "", phone: "", company: "", role: "Administrator" });
  
  const [products, setProducts]   = useState([]); 
  const [sales, setSales]         = useState([]); 
  const [customers, setCustomers] = useState([]); 
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  useEffect(() => {
    const settingsRef = doc(db, "settings", "main_workspace");

    getDoc(settingsRef).then((docSnap) => {
      if (!docSnap.exists()) {
        const initialData = {
          balances: { "FFC Alipay": 0, "FFC Wechat": 0 },
          notifications: { emailAlerts: true, stockAlerts: true, salesReports: false, newCustomers: true },
          profile: { name: "Admin", email: "admin@workspace.com", phone: "", company: "", role: "Administrator" }
        };
        setDoc(settingsRef, initialData).then(() => setSettingsLoaded(true));
      } else {
        setSettingsLoaded(true);
      }
    });

    const unsubSettings = onSnapshot(settingsRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.balances) setBalances(data.balances);
        if (data.notifications) setNotifications(data.notifications);
        if (data.profile) setProfile(data.profile);
      }
    });

    const unsubProd = onSnapshot(collection(db, "products"), (snap) => setProducts(snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))));
    const unsubSales = onSnapshot(collection(db, "sales"), (snap) => setSales(
      snap.docs.map((docSnap) => {
        const data = docSnap.data();
        return { docId: docSnap.id, ...data, id: data.id || docSnap.id };
      })
    ));
    const unsubCust = onSnapshot(collection(db, "customers"), (snap) => setCustomers(snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))));

    return () => { unsubSettings(); unsubProd(); unsubSales(); unsubCust(); };
  }, []);

  const addBalance = async (method, amount) => {
    try {
      const settingsRef = doc(db, "settings", "main_workspace");
      await updateDoc(settingsRef, { [`balances.${method}`]: increment(amount) });
    } catch (error) {
      console.error("Failed to add balance: ", error);
      alert("Error: Balance was not updated in the database.");
    }
  };

  const deductBalance = async (method, amount) => {
    try {
      const settingsRef = doc(db, "settings", "main_workspace");
      await updateDoc(settingsRef, { [`balances.${method}`]: increment(-amount) });
    } catch (error) {
      console.error("Failed to deduct balance: ", error);
      alert("Error: Balance was not deducted from the database.");
    }
  };

  const updateProfile = async (newProfile) => {
    await updateDoc(doc(db, "settings", "main_workspace"), { profile: newProfile });
    setProfile(newProfile);
  };

  if (!settingsLoaded) return (
    <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#FFFFFF", color: "#6366f1", fontWeight: 700 }}>
      Syncing Cloud Workspace...
    </div>
  );

  return (
    <StockContext.Provider value={{ 
      products, setProducts, sales, setSales, customers, setCustomers, balances, deductBalance, addBalance,
      profile, setProfile: updateProfile, notifications, setNotifications 
    }}>
      <GlobalResponsiveStyles />
      <DashboardLayout />
    </StockContext.Provider>
  );
}
